/**
 * Estado do formulário do protocolo de assinatura (E74.F1) e as conversões
 * puras de ida e volta com o contrato do servidor. Sem JSX, sem hooks.
 *
 * O formulário guarda tudo como string (input controlado); a conversão para o
 * contrato faz vazio → null, número → number e datetime-local → ISO. O caminho
 * inverso hidrata o formulário a partir do protocolo salvo.
 */
import { ptBR } from '@/i18n/pt-BR';
import type {
  EventoProtocoloData,
  FonteCoordenada,
  PapelIp,
  ProtocoloAssinaturaData,
  TipoAssinatura,
  TipoEventoProtocolo,
} from '@/lib/exame_eletronico';
import { formatCpf } from '@/lib/masks';

export type ProvaDeVidaForm = 'nao_informado' | 'sim' | 'nao';

export interface EventoForm {
  tipo: TipoEventoProtocolo;
  emUtc: string;
  emLocal: string;
  descricao: string;
}

export interface IpForm {
  endereco: string;
  papel: PapelIp;
}

export interface ProtocoloFormState {
  plataforma: string;
  codigoVerificacao: string;
  envelopeId: string;
  assinaturaId: string;
  hashDeclarado: string;
  tipoAssinatura: TipoAssinatura;
  signatarioNome: string;
  signatarioCpf: string;
  signatarioEmail: string;
  signatarioTelefone: string;
  eventos: EventoForm[];
  ips: IpForm[];
  latitude: string;
  longitude: string;
  precisaoM: string;
  fonteCoordenada: FonteCoordenada;
  userAgent: string;
  fingerprint: string;
  biometriaPresente: boolean;
  biometriaSessaoId: string;
  biometriaScore: string;
  biometriaThreshold: string;
  biometriaProvaDeVida: ProvaDeVidaForm;
  biometriaFornecedor: string;
  observacoes: string;
}

export interface ProtocoloFormErros {
  hashDeclarado?: string;
  eventos?: string;
  coordenada?: string;
}

const SHA256_HEX = /^[0-9a-f]{64}$/;
const DATETIME_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

export function eventoVazio(): EventoForm {
  return { tipo: 'assinatura', emUtc: '', emLocal: '', descricao: '' };
}

export function ipVazio(): IpForm {
  return { endereco: '', papel: 'cliente' };
}

export function estadoVazio(): ProtocoloFormState {
  return {
    plataforma: '',
    codigoVerificacao: '',
    envelopeId: '',
    assinaturaId: '',
    hashDeclarado: '',
    tipoAssinatura: 'nao_informado',
    signatarioNome: '',
    signatarioCpf: '',
    signatarioEmail: '',
    signatarioTelefone: '',
    eventos: [],
    ips: [],
    latitude: '',
    longitude: '',
    precisaoM: '',
    fonteCoordenada: 'nao_informada',
    userAgent: '',
    fingerprint: '',
    biometriaPresente: false,
    biometriaSessaoId: '',
    biometriaScore: '',
    biometriaThreshold: '',
    biometriaProvaDeVida: 'nao_informado',
    biometriaFornecedor: '',
    observacoes: '',
  };
}

// Datas -------------------------------------------------------------------

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

/** ISO (qualquer fuso) → valor de `datetime-local` lido em UTC. */
export function datetimeLocalDeIsoUtc(iso: string | null | undefined): string {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toISOString().slice(0, 16);
}

/** Valor de `datetime-local` digitado COMO UTC → ISO com `Z`. */
export function isoUtcDeDatetimeLocal(valor: string): string | null {
  const v = valor.trim();
  if (!DATETIME_LOCAL.test(v)) return null;
  return v.length === 16 ? `${v}:00Z` : `${v}Z`;
}

/** ISO (qualquer fuso) → valor de `datetime-local` no fuso deste navegador. */
export function datetimeLocalDeIsoLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return (
    `${d.getFullYear()}-${doisDigitos(d.getMonth() + 1)}-${doisDigitos(d.getDate())}` +
    `T${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}`
  );
}

/**
 * Valor de `datetime-local` (horário local do protocolo) → ISO com o deslocamento
 * do fuso deste navegador. É a melhor aproximação disponível: o protocolo
 * imprime o horário local sem dizer o fuso, e o escritório está no mesmo fuso
 * do signatário na quase totalidade dos casos.
 */
export function isoLocalDeDatetimeLocal(valor: string): string | null {
  const v = valor.trim();
  if (!DATETIME_LOCAL.test(v)) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const offsetMin = -d.getTimezoneOffset();
  const sinal = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const offset = `${sinal}${doisDigitos(Math.floor(abs / 60))}:${doisDigitos(abs % 60)}`;
  return `${v.length === 16 ? `${v}:00` : v}${offset}`;
}

// Normalizações -----------------------------------------------------------

/** Mesma normalização do servidor: tira espaços/dois-pontos e baixa a caixa. */
export function normalizarHash(valor: string): string {
  return valor.replace(/[\s:]/g, '').toLowerCase();
}

function textoOuNull(valor: string): string | null {
  const v = valor.trim();
  return v.length > 0 ? v : null;
}

function numeroOuNull(valor: string): number | null {
  const v = valor.trim().replace(',', '.');
  if (v.length === 0) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function provaDeVida(valor: ProvaDeVidaForm): boolean | null {
  if (valor === 'sim') return true;
  if (valor === 'nao') return false;
  return null;
}

function provaDeVidaForm(valor: boolean | null | undefined): ProvaDeVidaForm {
  if (valor === true) return 'sim';
  if (valor === false) return 'nao';
  return 'nao_informado';
}

// Ida e volta -------------------------------------------------------------

function texto(valor: string | null | undefined): string {
  return valor ?? '';
}

function numeroTexto(valor: number | null | undefined): string {
  return valor == null ? '' : String(valor);
}

function identificacaoForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  return {
    plataforma: texto(p.plataforma),
    codigoVerificacao: texto(p.codigo_verificacao),
    envelopeId: texto(p.envelope_id),
    assinaturaId: texto(p.assinatura_id),
    hashDeclarado: texto(p.hash_declarado),
    tipoAssinatura: p.tipo_assinatura,
    observacoes: texto(p.observacoes),
  };
}

function signatarioForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  return {
    signatarioNome: texto(p.signatario.nome),
    signatarioCpf: formatCpf(texto(p.signatario.cpf)),
    signatarioEmail: texto(p.signatario.email),
    signatarioTelefone: texto(p.signatario.telefone),
  };
}

function listasForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  return {
    eventos: p.eventos.map((e) => ({
      tipo: e.tipo,
      emUtc: datetimeLocalDeIsoUtc(e.em_utc),
      emLocal: datetimeLocalDeIsoLocal(e.em_local),
      descricao: texto(e.descricao),
    })),
    ips: p.ips.map((ip) => ({ endereco: ip.endereco, papel: ip.papel })),
  };
}

function coordenadaForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  const c = p.coordenada;
  if (c === null) return {};
  return {
    latitude: String(c.latitude),
    longitude: String(c.longitude),
    precisaoM: numeroTexto(c.precisao_m),
    fonteCoordenada: c.fonte,
  };
}

function dispositivoForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  if (p.dispositivo === null) return {};
  return {
    userAgent: texto(p.dispositivo.user_agent),
    fingerprint: texto(p.dispositivo.fingerprint),
  };
}

function biometriaForm(p: ProtocoloAssinaturaData): Partial<ProtocoloFormState> {
  const b = p.biometria;
  return {
    biometriaPresente: b.presente,
    biometriaSessaoId: texto(b.sessao_id),
    biometriaScore: numeroTexto(b.score),
    biometriaThreshold: numeroTexto(b.threshold),
    biometriaProvaDeVida: provaDeVidaForm(b.prova_de_vida),
    biometriaFornecedor: texto(b.fornecedor),
  };
}

export function estadoDoProtocolo(p: ProtocoloAssinaturaData | null): ProtocoloFormState {
  if (p === null) return estadoVazio();
  return {
    ...estadoVazio(),
    ...identificacaoForm(p),
    ...signatarioForm(p),
    ...listasForm(p),
    ...coordenadaForm(p),
    ...dispositivoForm(p),
    ...biometriaForm(p),
  };
}

function eventoTemHorario(e: EventoForm): boolean {
  return e.emUtc.trim().length > 0 || e.emLocal.trim().length > 0;
}

/** Validação do que o servidor recusaria com 422 — antes de enviar. */
export function validarProtocoloForm(s: ProtocoloFormState): ProtocoloFormErros {
  const t = ptBR.exameEletronico.form;
  const erros: ProtocoloFormErros = {};
  const hash = normalizarHash(s.hashDeclarado);
  if (hash.length > 0 && !SHA256_HEX.test(hash)) erros.hashDeclarado = t.hashDeclaradoErro;
  if (s.eventos.some((e) => !eventoTemHorario(e))) erros.eventos = t.eventoSemHorario;
  const temLat = s.latitude.trim().length > 0;
  const temLong = s.longitude.trim().length > 0;
  if (temLat !== temLong) erros.coordenada = t.coordenadaIncompleta;
  return erros;
}

function eventoDoForm(e: EventoForm): EventoProtocoloData {
  return {
    tipo: e.tipo,
    em_utc: isoUtcDeDatetimeLocal(e.emUtc),
    em_local: isoLocalDeDatetimeLocal(e.emLocal),
    descricao: textoOuNull(e.descricao),
  };
}

function coordenadaDoForm(s: ProtocoloFormState): ProtocoloAssinaturaData['coordenada'] {
  const latitude = numeroOuNull(s.latitude);
  const longitude = numeroOuNull(s.longitude);
  if (latitude === null || longitude === null) return null;
  return { latitude, longitude, precisao_m: numeroOuNull(s.precisaoM), fonte: s.fonteCoordenada };
}

function dispositivoDoForm(s: ProtocoloFormState): ProtocoloAssinaturaData['dispositivo'] {
  const user_agent = textoOuNull(s.userAgent);
  const fingerprint = textoOuNull(s.fingerprint);
  if (user_agent === null && fingerprint === null) return null;
  return { user_agent, fingerprint };
}

export function protocoloDoEstado(s: ProtocoloFormState): ProtocoloAssinaturaData {
  const hash = normalizarHash(s.hashDeclarado);
  return {
    plataforma: textoOuNull(s.plataforma),
    codigo_verificacao: textoOuNull(s.codigoVerificacao),
    envelope_id: textoOuNull(s.envelopeId),
    assinatura_id: textoOuNull(s.assinaturaId),
    hash_declarado: hash.length > 0 ? hash : null,
    tipo_assinatura: s.tipoAssinatura,
    signatario: {
      nome: textoOuNull(s.signatarioNome),
      cpf: textoOuNull(s.signatarioCpf),
      email: textoOuNull(s.signatarioEmail),
      telefone: textoOuNull(s.signatarioTelefone),
    },
    eventos: s.eventos.map(eventoDoForm),
    ips: s.ips
      .filter((ip) => ip.endereco.trim().length > 0)
      .map((ip) => ({ endereco: ip.endereco.trim(), papel: ip.papel })),
    coordenada: coordenadaDoForm(s),
    dispositivo: dispositivoDoForm(s),
    biometria: {
      presente: s.biometriaPresente,
      sessao_id: textoOuNull(s.biometriaSessaoId),
      score: numeroOuNull(s.biometriaScore),
      threshold: numeroOuNull(s.biometriaThreshold),
      prova_de_vida: provaDeVida(s.biometriaProvaDeVida),
      fornecedor: textoOuNull(s.biometriaFornecedor),
    },
    observacoes: textoOuNull(s.observacoes),
  };
}
